import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import type { Lang } from '../../config/locales'
import { controlClass, labelClass } from '../forms/fieldStyle'
import { formatKr } from '../../lib/shop/format'
import { MAX_CODE_LENGTH, refusalMessage } from '../../lib/shop/discountCode'
import { useDiscountLockSeconds } from '../../lib/shop/discountLock'
import type { Discount } from '../../lib/shop/useDiscount'

interface DiscountCodeFieldProps {
  discount: Discount
  locale: Lang
  /** Unique per place (basket, checkout desktop, checkout phone), for the field's id. */
  idPrefix: string
}

/**
 * "Har du en rabatkode?" (BUG-518): a link that opens a field and Apply. An applied code shows as
 * a line with Remove; a code that does not apply (any more) stays in the field with the reason
 * under it. The message sits in one live region, so a screen reader hears the answer.
 * BUG-519: after six failed codes in a minute the field and Apply are disabled, with the seconds left,
 * and open again by themselves.
 */
export default function DiscountCodeField({ discount, locale, idPrefix }: DiscountCodeFieldProps) {
  const { t } = useTranslation()
  const { code, state } = discount
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(code)
  const [empty, setEmpty] = useState(false)
  // The code changed elsewhere (the basket and checkout share it, or Remove): the field follows.
  const [shown, setShown] = useState(code)
  if (shown !== code) {
    setShown(code)
    setDraft(code)
  }
  const inputRef = useRef<HTMLInputElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const removeRef = useRef<HTMLButtonElement>(null)
  // Where focus goes after the field changes shape under the shopper's hand.
  const focusNext = useRef<'input' | 'toggle' | 'remove' | null>(null)

  const applied = state.kind === 'applied'
  const expanded = open || code !== ''
  const lockSeconds = useDiscountLockSeconds()
  const locked = lockSeconds > 0
  // The seconds when the lock began: the live region says that once, the visible text counts down.
  const [announced, setAnnounced] = useState(0)
  if (locked ? announced === 0 : announced !== 0) setAnnounced(locked ? lockSeconds : 0)

  useEffect(() => {
    const target = focusNext.current
    if (!target) return
    if (target === 'remove') {
      // After Apply: wait for the answer, then focus Remove on the applied line, or stay in the field.
      if (state.kind === 'checking') return
      focusNext.current = null
      ;(applied ? removeRef : inputRef).current?.focus()
      return
    }
    focusNext.current = null
    ;(target === 'input' ? inputRef : toggleRef).current?.focus()
  })

  const inputId = `${idPrefix}-code`
  const messageId = `${idPrefix}-code-msg`
  const error = locked
    ? t('discount.errors.tooManyAttempts', { count: lockSeconds })
    : empty
      ? t('discount.errors.empty')
      : state.kind === 'refused'
        ? (({ key, values }) => t(key, values))(refusalMessage(state.refusal, (n) => formatKr(n, locale)))
        : state.kind === 'failed'
          ? t('discount.errors.unavailable')
          : null

  const submit = () => {
    if (!discount.apply(draft)) {
      setEmpty(true)
      inputRef.current?.focus()
      return
    }
    setEmpty(false)
    focusNext.current = 'remove'
  }
  const remove = () => {
    discount.remove()
    setOpen(false)
    setEmpty(false)
    focusNext.current = 'toggle'
  }

  return (
    <div data-discount={idPrefix}>
      {!expanded ? (
        <button
          ref={toggleRef}
          type="button"
          aria-expanded={false}
          onClick={() => {
            setOpen(true)
            focusNext.current = 'input'
          }}
          className="inline-flex min-h-11 items-center font-body text-sm text-gold-ink underline underline-offset-2 hover:text-gold"
        >
          {t('discount.toggle')}
        </button>
      ) : applied ? (
        <div className="flex min-h-11 items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2 font-body text-sm text-mocha">
            <Check size={16} className="shrink-0 text-gold-ink" aria-hidden="true" />
            <span className="min-w-0 break-all">
              {t('discount.label')}: <span className="font-semibold tracking-wide">{code}</span>
            </span>
          </span>
          <button
            ref={removeRef}
            type="button"
            onClick={remove}
            aria-label={t('discount.removeNamed', { code })}
            className="min-h-11 shrink-0 font-body text-sm text-gold-ink underline underline-offset-2 hover:text-gold"
          >
            {t('discount.remove')}
          </button>
        </div>
      ) : (
        <div className="flex flex-col">
          <label htmlFor={inputId} className={labelClass('stacked', true)}>
            {t('discount.label')}
          </label>
          <div className="flex gap-2">
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value)
                setEmpty(false)
              }}
              onKeyDown={(e) => {
                // Enter applies the code; it never submits the checkout form around it.
                if (e.key === 'Enter') {
                  e.preventDefault()
                  submit()
                }
              }}
              maxLength={MAX_CODE_LENGTH}
              disabled={locked}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? messageId : undefined}
              className={controlClass('stacked', Boolean(error), 'min-w-0 flex-1 uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-60')}
            />
            <button
              type="button"
              onClick={submit}
              disabled={locked || state.kind === 'checking'}
              className={`min-h-11 shrink-0 rounded-md border border-mocha/25 bg-white px-4 font-body text-sm text-mocha transition-colors hover:border-gold hover:text-gold-ink disabled:opacity-60 lg:min-h-10 ${locked ? 'disabled:cursor-not-allowed' : 'disabled:cursor-wait'}`}
            >
              {state.kind === 'checking' && !locked ? t('discount.applying') : t('discount.apply')}
            </button>
          </div>
          {code && (
            <button
              ref={removeRef}
              type="button"
              onClick={remove}
              aria-label={t('discount.removeNamed', { code })}
              className="mt-1 min-h-11 self-start font-body text-sm text-gold-ink underline underline-offset-2 hover:text-gold"
            >
              {t('discount.remove')}
            </button>
          )}
        </div>
      )}
      {/* One live region, always there, so the answer is announced as it arrives. */}
      <p id={messageId} aria-live="polite" className={error ? 'mt-1.5 font-body text-xs text-red-700' : 'sr-only'}>
        {locked ? (
          <>
            <span aria-hidden="true">{error}</span>
            <span className="sr-only">{t('discount.errors.tooManyAttempts', { count: announced || lockSeconds })}</span>
          </>
        ) : (
          (error ?? (applied ? t('discount.applied', { code }) : ''))
        )}
      </p>
    </div>
  )
}
